import { defineStore } from 'pinia';
import { ref, computed, watch, toRaw } from 'vue';
import {
    getLibraryNames,
    getMyUserName,
    SavedQuerySaveRequest,
    ViewDefinitionDTO,
    SavedQueryHeaderInfo,
    getAvailableQueries,
    getSavedQueryByGuid,
    sqlQuery,
    SqlQueryRequest,
    SavedQueryUsageUpdateRequest
} from '@/shared/helpers/api.ts';
import { isAdministrator } from '@/shared/directives/requirePermission';
import { AttributeDefinition } from '@/shared/helpers/api/types/entities.ts';
import { http } from '@/shared/httpWrapper.js';
import _ from 'lodash';
import {
    LibraryDefinition,
    loadLibraryDefinition
} from '@/apps/sqlEditor/sqlEditor.libraryloading';
import {
    getRecentLibraries,
    addRecentLibrary,
    removeRecentLibrary,
    refreshRecentLibraries
} from '@/apps/sqlEditor/sqlEditor.recentlibrarydefinitions';
import { getParameters } from '@/apps/sqlEditor/sqlParameterCrawler';
import { parseError } from '@/shared/helpers/errorMessageParser';

/*
    This store is organized into logical sections with only limited
    dependencies between them, and everything is defined before referenced.
    Ref and action names also reflect the section they belong to.
    Actions are grouped with the state they belong to.
 */

export type LoadedQueryParameter = {
    name: string;
    stringValue: string;
};

export type AutocompleteSchema = {
    [library: string]: string[];
};

export const useSqlEditorStore = defineStore('sqlEditor', () => {
    const userId = ref<string | null>(null);
    const userIsAdministrator = ref<boolean>(false);

    /* ******************************************************************** *
        LOADED QUERY - State for one loaded query
     * ******************************************************************** */
    const loadedQueryId = ref<number | null>(null);
    const loadedQueryGuid = ref<string | null>(null);
    const loadedQueryIsPublic = ref(false);
    const loadedQueryIsShared = ref(false);
    const loadedQueryName = ref('');
    const loadedQueryOriginalIsPublic = ref(false);
    const loadedQueryOriginalName = ref('');
    const loadedQueryPageSize = ref<number | null>(null);
    const loadedQuerySql = ref('');
    const loadedQueryUserId = ref<string | null>(null);
    const loadedQueryUserName = ref<string | null>(null);
    const loadedQueryParameters = ref<LoadedQueryParameter[]>([]);
    const loadedQueryUsage = ref<string | null>(null);
    const loadedQueryUsedBy = ref<string | null>(null);
    const loadedQueryUsedByExternalApplication = ref<boolean>(false);

    const queryIsLoaded = computed(() =>
        loadedQueryId.value !== null);

    const loadedQueryOwnedByCurrentUser = computed(() =>
        queryIsLoaded.value && loadedQueryUserId.value === userId.value);

    const maxQueryNameLength = ref<number>(256);

    watch(loadedQuerySql, (newValue) => {
        if (!loadedQuerySql.value) {
            loadedQueryParameters.value = [];
        } else {
            try {
                const parameterNames = getParameters(newValue);

                // TODO: Handle different casing

                parameterNames.forEach(name => {
                    const existingParameter = loadedQueryParameters.value.find(x => x.name === name);
                    if (!existingParameter) {
                        loadedQueryParameters.value.push({
                            name,
                            stringValue: ''
                        });
                    }
                });

                // Remove any parameters no longer found in SQL
                loadedQueryParameters.value = loadedQueryParameters.value.filter(x => parameterNames.includes(x.name));
            } catch {
                // Ignore errors, as unfinished SQL must be ok
            }
        }
    });

    async function loadedQuerySet(guid: string): Promise<void> {
        const query = await getSavedQueryByGuid(http, guid);
        loadedQueryId.value = query.id;
        loadedQueryGuid.value = query.guid;
        loadedQuerySql.value = query.spec;
        loadedQueryUserId.value = query.userId;
        loadedQueryOriginalName.value = query.name;
        loadedQueryName.value = query.name;
        loadedQueryIsPublic.value = query.isPublic;
        loadedQueryIsShared.value = query.isShared;
        loadedQueryOriginalIsPublic.value = query.isPublic;
        loadedQueryPageSize.value = query.pageSize;
        loadedQueryUsage.value = query.usage;
        loadedQueryUsedBy.value = query.usedBy;
        loadedQueryUsedByExternalApplication.value = query.usedByExternalApplication;

        // Get the username from store state
        loadedQueryUserName.value = savedQueryGetByGuid(guid)?.userName ?? null;
    }

    function loadedQuerySetCurrentOriginal(): void {
        loadedQueryOriginalName.value = loadedQueryName.value;
        loadedQueryOriginalIsPublic.value = loadedQueryIsPublic.value;
    }

    // Updates the usage for the loaded query in the store only
    function loadedQueryUpdateUsage(request: SavedQueryUsageUpdateRequest): void {
        loadedQueryUsage.value = request.usage;
        loadedQueryUsedBy.value = request.usedBy;
        loadedQueryUsedByExternalApplication.value = request.usedByExternalApplication;
    }

    function loadedQueryUnset() {
        loadedQueryId.value = null;
        loadedQueryGuid.value = null;
        loadedQueryUserId.value = null;
        loadedQuerySql.value = '';
        loadedQueryName.value = '';
        loadedQueryOriginalName.value = '';
        loadedQueryIsPublic.value = false;
        loadedQueryIsShared.value = false;
        loadedQueryOriginalIsPublic.value = false;
        loadedQueryUserName.value = null;
        loadedQueryPageSize.value = null;
    }

    function loadedQueryGetSaveRequest(): SavedQuerySaveRequest {
        return {
            name: loadedQueryName.value.trim(),
            spec: loadedQuerySql.value,
            isPublic: loadedQueryIsPublic.value,
            isShared: loadedQueryIsShared.value,
            pageSize: loadedQueryPageSize.value || undefined,
            queryType: 1 // == SQL
        };
    }

    function loadedQueryGetRunRequest(): SqlQueryRequest {
        /*
            As Dapper is very forgiving when it comes to parameters,
            it's fine to send them all inn as strings.
        */
        return {
            query: loadedQuerySql.value,
            arguments: loadedQueryParameters.value.reduce((acc, param) => {
                acc[param.name] = param.stringValue;
                return acc;
            }, {} as Record<string, string>)
        };
    }

    /* ******************************************************************** *
        RUN - Running the current query
     * ******************************************************************** */

    async function runQuery(): Promise<void> {
        try {
            errorClear();
            const result = await sqlQuery(http, loadedQueryGetRunRequest());
            resultSet(result.data);
        } catch (ex) {
            errorSet(parseError(ex as object));
        }
    }

    /* ******************************************************************** *
        RESULT - The result of an executed query
     * ******************************************************************** */
    const resultId = ref<number>(0);
    const result = ref<Record<string, unknown>[] | null>(null);

    const resultColumns = computed(() => {
        if (result.value && result.value.length > 0) {
            const fieldNames = _.keys(result.value[0]);
            return _.map(fieldNames, fn => ({
                field: fn,
                label: fn,
                sortable: true
            }));
        }
        return [];
    });

    function resultSet(value: Record<string, unknown>[]) {
        result.value = value;
        resultId.value += 1;
    }

    /* ******************************************************************** *
        ERROR - An error for executing a query
     * ******************************************************************** */
    const errorMessage = ref<string | null>(null);

    function errorSet(value: string) {
        errorMessage.value = value;
    }

    function errorClear() {
        errorMessage.value = null;
    }

    /* ******************************************************************** *
        LIBRARY - List of all library names, where one can be selected
     * ******************************************************************** */
    const libraryNames = ref<string[]>([]);
    const librarySelected = ref<string | null>(null);

    function libraryGetSelected() {
        return librarySelected.value;
    }

    function librarySetSelected(value: string) {
        if (librarySelected.value !== value)
            librarySelected.value = value;
    }

    /* ******************************************************************** *
        Tables - List of library table/view definitions
     * ******************************************************************** */
    const tables = ref<LibraryDefinition[]>([]);
    let recentTableRefreshId: number | null = null;

    function tablesSort() {
        tables.value.sort((a, b) => a.view.library.toUpperCase() > b.view.library.toUpperCase() ? 1 : -1);
    }

    async function tablesAdd(view: ViewDefinitionDTO, attributes: AttributeDefinition[]): Promise<void> {
        const alreadyAdded = tables.value.find(x => x.view.library === view.library);
        tables.value.forEach(x => x.isOpen = false);
        if (alreadyAdded) {
            alreadyAdded.isOpen = true;
        } else {
            tables.value.push({ view, attributes, isOpen: true });
            tablesSort();
            await addRecentLibrary({ view: toRaw(view), attributes: toRaw(attributes), isOpen: true });
        }
    }

    async function tablesRemove(libraryName: string): Promise<void> {
        const idx = tables.value.findIndex((e) => e.view.library === libraryName);
        if (idx !== -1) {
            tables.value.splice(idx, 1);
            await removeRecentLibrary(libraryName);
        }
    }

    async function initRecentTables() {
        const recentList = await getRecentLibraries();
        recentList.forEach(x => {
            if (x !== null) {
                // Due to possible race condition between init() and watchers calling tablesAdd()
                // we need to ensure that we don't add duplicates.
                const alreadyAdded = tables.value.some(y => x.view.library === y.view.library);
                if (!alreadyAdded) {
                    x.isOpen = false;
                    tables.value.push(x);
                }
            }
        });
        tablesSort();

        // Initiate a persisted cache refresh check to be performed in a few seconds
        recentTableRefreshId ??= window.setTimeout(
            async () => {
                const allowedAgeLimit = 60 * 24; // 24 hours
                await refreshRecentLibraries(allowedAgeLimit, loadLibraryDefinition);
                recentTableRefreshId = null;
            },
            1000 * 4 // Four seconds
        );
    }

    /* ******************************************************************** *
        SAVED QUERIES - List of all saved queries
     * ******************************************************************** */
    const savedQueriesAll = ref<SavedQueryHeaderInfo[]>([]);

    function savedQueryGetByGuid(guid: string): SavedQueryHeaderInfo | undefined {
        return savedQueriesAll.value.find(x => x.guid === guid);
    }

    function isSqlQuery(q: SavedQueryHeaderInfo) {
        return q.queryType === 1;
    }

    async function savedQueriesReload() {
        const queries = await getAvailableQueries(http);
        savedQueriesAll.value = queries.filter(isSqlQuery);
    }

    /* ******************************************************************** *
        Library schema for autocompletion in editor
     * ******************************************************************** */

    type SchemaQueryResult = {
        library: string;
        columns: string;
    }[];

    type AutocompleteCacheEntry = {
        schema: AutocompleteSchema;
        timestamp: number;
    };

    async function getLibrarySchema(): Promise<AutocompleteSchema> {
        const storedValue = localStorage.getItem('sqlEditorAutocompleteSchemaCache');

        if (storedValue) {
            const { schema, timestamp }: AutocompleteCacheEntry = JSON.parse(storedValue);
            if (Date.now() - timestamp > 60 * 60 * 1000) {
                return await updateLibrarySchemaCache();
            } else {
                return schema;
            }
        } else {
            return await updateLibrarySchemaCache();
        }
    }

    async function updateLibrarySchemaCache(): Promise<AutocompleteSchema> {
        const data = (await sqlQuery(http, {
            query: 'SELECT Library AS library, STRING_AGG(Name, \',\') AS columns FROM _column GROUP BY Library'
        })).data as SchemaQueryResult;

        const schema: AutocompleteSchema = {};
        data.forEach((lib) => {
            schema[lib.library] = lib.columns.split(',');
        });

        localStorage.setItem('sqlEditorAutocompleteSchemaCache', JSON.stringify({
            schema: schema,
            timestamp: Date.now()
        }));

        return schema;
    }

    function getLibrarySchemaTimestamp(): number | null {
        const storedValue = localStorage.getItem('sqlEditorAutocompleteSchemaCache');
        if (!storedValue) {
            return null;
        } else {
            const { timestamp }: AutocompleteCacheEntry = JSON.parse(storedValue);
            return timestamp;
        }
    }

    /* ******************************************************************** *
        Init() and some UI state variables
     * ******************************************************************** */

    // If true, the user can commit the save operation for a new query or a
    // loaded query that he is the owner of.
    const canSave = computed(() =>
        // there is a name
        loadedQueryName.value.trim().length > 0
        // and name is not too long
        && loadedQueryName.value.trim().length < maxQueryNameLength.value
        // and new query, owned query, or admin user
        && (userIsAdministrator.value || !queryIsLoaded.value || loadedQueryOwnedByCurrentUser.value)
        // and NOT: was not public, now set to public, but no admin
        && !(!loadedQueryOriginalIsPublic.value && loadedQueryIsPublic.value && !userIsAdministrator.value)
    );

    async function init() {
        userId.value = await getMyUserName(http);
        userIsAdministrator.value = await isAdministrator();
        libraryNames.value = (await getLibraryNames(http)).sort();
        await savedQueriesReload();
        await initRecentTables();
    }

    return {
        runQuery,

        errorMessage,
        errorSet,
        errorClear,

        libraryNames,
        librarySelected,
        libraryGetSelected,
        librarySetSelected,

        loadedQueryId,
        loadedQueryGuid,
        loadedQueryIsPublic,
        loadedQueryIsShared,
        loadedQueryOriginalIsPublic,
        loadedQueryName,
        loadedQueryOriginalName,
        loadedQueryOwnedByCurrentUser,
        loadedQueryPageSize,
        loadedQueryParameters,
        loadedQuerySql,
        loadedQueryUserId,
        loadedQueryUserName,
        loadedQueryUsage,
        loadedQueryUsedBy,
        loadedQueryUsedByExternalApplication,
        queryIsLoaded,
        loadedQueryGetSaveRequest,
        loadedQueryGetRunRequest,
        loadedQuerySet,
        loadedQuerySetCurrentOriginal,
        loadedQueryUnset,
        loadedQueryUpdateUsage,
        maxQueryNameLength,

        result,
        resultId,
        resultColumns,
        resultSet,

        savedQueriesAll,
        savedQueriesReload,

        getLibrarySchema,
        updateLibrarySchemaCache,
        getLibrarySchemaTimestamp,

        tables,
        tablesAdd,
        tablesRemove,

        canSave,
        userId,
        userIsAdministrator,
        init
    };
});
